// core
import { useMemo } from "react";
import { useImmer } from "use-immer";

// interfaces
import { IClient } from "../../common/interfaces/IClient";
import { IQuoteCardResult } from "../interfaces/results/IQuoteCardResult";
import { IQuoteGroupState } from "../interfaces/IQuoteGroupState";

// types
import { QuoteSettingContainerType } from "../../common/interfaces/settings/QuoteSettingContainerType";
import ProductType, {
    ClientProductTypes,
    ClientProductTypesList
} from "../../common/interfaces/enums/ProductType";
import { IQuoteGroupStateSelectors } from "../interfaces/IQuoteGroupStateSelectors";
import { IContactDetails } from "../../common/interfaces/IContactDetails";
import { IQuoteGroupStateActions } from "../interfaces/IQuoteGroupStateActions";
import { IQuoteSettingsContainerBase } from "../../common/interfaces/settings/IQuoteSettingsContainerBase";
import { QuoteSettingsType } from "../types/QuoteSettingType";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { QuoteTableView } from "../enums/QuoteTableView";
import { PaymentFrequency } from "../../common/interfaces/enums/PaymentFrequency";
import { ISelectedAddOn } from "../interfaces/results/ISelectedAddOn";
import { IProviderAddon } from "../interfaces/IProviderAddon";
interface IQuoteGroupStateReturns {
    selectors: IQuoteGroupStateSelectors;
    actions: IQuoteGroupStateActions;
}

const useQuoteGroupState = (defaultState: IQuoteGroupState): IQuoteGroupStateReturns => {
    const [state, setState] = useImmer<IQuoteGroupState>(defaultState);
    const ErrorLogger = useErrorLogger();

    return {
        selectors: {
            isNewSession: () => {
                // A check for if the user is starting a new 'session'
                // - Do we have an client information for them?
                // - Are the current clients stored in the DB, with a valid person Id?
                return (
                    state.clients.length === 0 ||
                    !(state.clients.filter((c) => c.personId !== 0).length > 0)
                );
            },
            getGroupId: (): string => {
                return state.groupId;
            },
            isAdmin: (): boolean => {
                return state.isAdmin;
            },
            isFromCrm: (): boolean => {
                return state.isFromCrm;
            },
            // Contact details
            getContactDetails: function (): IContactDetails {
                return state.contactDetails;
            },
            hasContactDetails: function (): boolean {
                return state.contactDetails.hasContactDetails;
            },
            // Clients
            getClients: function (): IClient[] {
                return state.clients.filter((c: IClient) => c.isActive) || [];
            },
            // Selected products
            hasAnyProductEnabled: function (): boolean {
                return ClientProductTypesList.some((p) => {
                    return this.isProductEnabled(p);
                });
            },
            isProductEnabled: function (productType: ClientProductTypes) {
                return state.quoteConfigs[productType].isEnabled;
            },
            // Quote settings
            getQuoteSettingByProductType: function (
                productType: ClientProductTypes
            ): QuoteSettingContainerType {
                return state.quoteConfigs[productType]?.config;
            },
            hasQuoteSettingsForProduct: function (
                productType: ClientProductTypes
            ): boolean {
                return this.getQuoteSettingByProductType(productType).caseId > 0;
            },
            getAllSettings: function () {
                return Object.values(state.quoteConfigs).map((s) => s.config);
            },
            // Quote results
            getQuoteResults: function (): IQuoteCardResult[] {
                return state.quoteResults.cards;
            },
            getQuoteCardResult: function (providerId: number): IQuoteCardResult {
                return state.quoteResults.cards.find(
                    (c) => c.result.providerId == providerId);
            },
            hasCreatedQuote: function (): boolean {
                return state.quoteResults.hasCreatedQuotes;
            },
            isLoadingQuoteResult: function (): boolean {
                return state.quoteResults.isLoading;
            },
            isQuoteResultValid: function (): boolean {
                return state.quoteResults.isValid;
            },
            getPaymentFrequency: function (): PaymentFrequency {
                return state.quoteResults.paymentFrequency;
            },
            getCurrentTableView: function (): QuoteTableView {
                return state.currentView;
            },
            isStreamlineFlow: function (): boolean {
                return state.isStreamlineFlow;
            },
            getProviderAddOns: function (): IProviderAddon[] {
                return state.quoteResults.cards.reduce(
                    (acc, val) => acc.concat(val.providerAddons),
                    []
                ); // todo esp null checking
            },
            getSelectedAddOns: function (providerId: number): ISelectedAddOn[] {
                return state.quoteResults.cards
                    .filter((q) => q.result.providerId === providerId)
                    .reduce(
                        (acc, val) =>
                            acc.concat(
                                val.result.selectedAddOns.filter((a) => a.isSelected)
                            ),
                        []
                    ); // todo esp null checking
            },
            isProviderAddOnSelected: function (addOnSysName: string): boolean {
                return state.quoteResults.cards
                    .reduce((acc, val) => acc.concat(val.result.selectedAddOns), [])
                    .some((a) => a.providerAddOnSysName === addOnSysName && a.isSelected);
            },
            // digital advice
            getDigitalAdviceURL: function (): string {
                const { sessionUserId, visitorId } = JSON.parse(
                    window.localStorage.getItem("dap_recommendation")
                );
                return `/compare/digital-advice/our-advice?sessionUserId=${sessionUserId}&visitorId=${visitorId}`;
            },
            hasDigitalAdviceResult: function (): boolean {
                return !!state["digital-advice"];
            }
        },
        actions: useMemo(
            (): IQuoteGroupStateActions => ({
                setGroupId: (groupId: string): void => {
                    ErrorLogger.SetUserData(groupId);
                    setState((draft: IQuoteGroupState) => {
                        draft.groupId = groupId;
                    });
                },
                setIsFromCrm: (isFromCrm: boolean): void => {
                    setState((draft: IQuoteGroupState) => {
                        return {
                            ...draft,
                            isFromCrm: isFromCrm
                        };
                    });
                },
                resetState: function (groupId: string): void {
                    setState((draft: IQuoteGroupState) => {
                        return {
                            ...defaultState,
                            isAdmin: draft.isAdmin,
                            groupId
                        };
                    });
                },
                // Clients
                setClients: function (clients: IClient[]): void {
                    setState((draft) => {
                        draft.clients = [...clients];
                    });
                    this.clearQuoteResults();
                },
                // Quote configs
                setQuoteConfig: function (settings: QuoteSettingContainerType) {
                    setState((draft: IQuoteGroupState) => {
                        const handlePopulation = <T extends IQuoteSettingsContainerBase>(
                            container: T
                        ): T => {
                            const settingsClients: QuoteSettingsType[] =
                                container.clients;

                            const clients = draft.clients
                                .map((client) => {
                                    const index = settingsClients.findIndex(
                                        (c) => c.personId === client.personId
                                    );

                                    if (settingsClients[index]) {
                                        // If the api returns client settings, we use those.
                                        return settingsClients[index];
                                    } else {
                                        // Otherwise we maintain the existing for this session.
                                        const oldConfigClients: QuoteSettingsType[] =
                                            draft.quoteConfigs[container.productType]
                                                ?.config.clients;
                                        return {
                                            ...oldConfigClients?.find(
                                                (c) => c.personId === client.personId
                                            ),
                                            isSelected: false
                                        };
                                    }
                                })
                                .filter((client) => client); //Remove null/undefined

                            return Object.freeze({
                                ...draft.quoteConfigs[container.productType],
                                ...container,
                                clients
                            });
                        };

                        if (settings.productType === ProductType.Life) {
                            draft.quoteConfigs.life.config = handlePopulation(settings);
                        } else if (settings.productType === ProductType.Health) {
                            draft.quoteConfigs.health.config = handlePopulation(settings);
                        } else if (settings.productType === ProductType.Trauma) {
                            draft.quoteConfigs.trauma.config = handlePopulation(settings);
                        } else if (settings.productType === ProductType.Income) {
                            draft.quoteConfigs.income.config = handlePopulation(settings);
                        } else if (settings.productType === ProductType.Mortgage) {
                            draft.quoteConfigs.mortgage.config = handlePopulation(
                                settings
                            );
                        } else {
                            throw new Error("Could not populate container");
                        }
                        draft.quoteConfigs[settings.productType].isEnabled = true;
                        draft.quoteResults.cards = [];
                        draft.quoteResults.isValid = false;
                    });
                },
                populateQuoteConfigs: function (
                    settingsList: QuoteSettingContainerType[]
                ): void {
                    settingsList.forEach(this.setQuoteConfig);
                },
                clearQuoteResults: function (): void {
                    setState((draft: IQuoteGroupState) => {
                        draft.quoteResults.cards = [];
                        draft.quoteResults.isValid = false;
                    });
                },
                setQuoteResults: function (results: IQuoteCardResult[]): void {
                    const keyedResults = results.map((card, index) => {
                        return {
                            ...card,
                            cardKey: index
                        };
                    });

                    setState((draft: IQuoteGroupState) => {
                        draft.quoteResults.cards = keyedResults;
                        draft.quoteResults.hasCreatedQuotes = true;
                        draft.quoteResults.isValid = true;
                    });
                },
                setPaymentFrequency: function (freq: PaymentFrequency): void {
                    setState((draft) => {
                        const existingFrequency = draft.quoteResults.paymentFrequency;
                        if (existingFrequency !== freq) {
                            draft.quoteResults.paymentFrequency = freq;
                            draft.quoteResults.isValid = false;
                        }
                    });
                },
                // Contact details
                setContactDetails: function (contactDetails: IContactDetails): void {
                    setState((draft: IQuoteGroupState) => {
                        draft.contactDetails = contactDetails;
                        draft.contactDetails.hasContactDetails = true;
                    });
                },
                setHasContactDetails: function (hasContactDetails: boolean): void {
                    setState((draft: IQuoteGroupState) => {
                        draft.contactDetails.hasContactDetails = hasContactDetails;
                    });
                },
                setProductEnabled: function (
                    productType: ClientProductTypes,
                    isEnabled: boolean
                ): void {
                    setState((draft) => {
                        draft.quoteConfigs[productType].isEnabled = isEnabled;
                    });
                },
                setIsLoadingQuoteResults: function (isLoading: boolean): void {
                    setState((draft: IQuoteGroupState) => {
                        draft.quoteResults.isLoading = isLoading;
                    });
                },
                // UI
                setCurrentTableView: function (view: QuoteTableView) {
                    setState((draft: IQuoteGroupState) => {
                        draft.currentView = view;
                    });
                },
                setIsStreamlineFlow: function (isStreamlineFlow: boolean) {
                    setState((draft: IQuoteGroupState) => {
                        draft.isStreamlineFlow = isStreamlineFlow;
                    });
                },
                setIsProviderAddOnSelected: function (
                    addOnSysName: string,
                    isSelected: boolean
                ): void {
                    setState((draft: IQuoteGroupState) => {
                        const cards = draft.quoteResults.cards.map((card) => {
                            let discountAmount = 0.0;
                            const selectedAddOns = card.result.selectedAddOns.map(
                                (addOn) => {
                                    if (addOn.providerAddOnSysName !== addOnSysName) return addOn;

                                    discountAmount = addOn.discountAmount;
                                    return { ...addOn, isSelected: isSelected };
                                }
                            );

                            const newTotalPremium = isSelected
                                ? card.result.totalPremium - discountAmount
                                : card.result.totalPremium + discountAmount;

                            const newRatings = {
                                ...card.ratings,
                                premium: {
                                    orderBy: newTotalPremium,
                                    title: "Price",
                                    ratingInfo: newTotalPremium
                                }
                            };

                            const newResult = {
                                ...card.result,
                                totalPremium: newTotalPremium,
                                selectedAddOns: selectedAddOns
                            };

                            return { ...card, result: newResult, ratings: newRatings };
                        });

                        draft.quoteResults.cards = cards;
                    });
                },
                // digital advice
                setDigitalAdvice: function (id: string) {
                    setState((draft: IQuoteGroupState) => {
                        draft["digital-advice"] = id;
                    });
                }
            }),
            [setState]
        )
    };
};

export default useQuoteGroupState;
